import React from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { BrandingProvider } from "./context/BrandingContext";
import { PWAProvider } from "./context/PWAContext";
import { FeaturesProvider } from "./context/FeaturesContext";
import { TenantProvider } from "./context/TenantContext";
import OfflineBadge from "./components/pwa/OfflineBadge";
import InstallBanner from "./components/pwa/InstallBanner";

import StoreHeader from "./components/layout/StoreHeader";
import StoreFooter from "./components/layout/StoreFooter";
import DashboardLayout from "./components/layout/DashboardLayout";

import Home from "./pages/store/Home";
import Listing from "./pages/store/Listing";
import ProductDetail from "./pages/store/ProductDetail";
import Cart from "./pages/store/Cart";
import Wishlist from "./pages/store/Wishlist";
import Checkout from "./pages/store/Checkout";
import OrderSuccess from "./pages/store/OrderSuccess";
import OrderDetail from "./pages/store/OrderDetail";
import Account from "./pages/store/Account";
import CampaignPage from "./pages/store/CampaignPage";
import SharedCartView from "./pages/store/SharedCartView";
import StyleFeed from "./pages/store/StyleFeed";
import CreatorPage from "./pages/store/CreatorPage";
import LegalPage from "./pages/LegalPage";

import SellerOnboard from "./pages/seller/SellerOnboard";
import SellerDashboard from "./pages/seller/SellerDashboard";
import SellerProducts from "./pages/seller/SellerProducts";
import SellerProductForm from "./pages/seller/SellerProductForm";
import SellerOrders from "./pages/seller/SellerOrders";
import SellerInventory from "./pages/seller/SellerInventory";
import SellerCoupons from "./pages/seller/SellerCoupons";
import SellerAnalytics from "./pages/seller/SellerAnalytics";
import SellerSettings from "./pages/seller/SellerSettings";

import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminSellers from "./pages/admin/AdminSellers";
import AdminCategories from "./pages/admin/AdminCategories";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminPingbix from "./pages/admin/AdminPingbix";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminBulkUpload from "./pages/admin/AdminBulkUpload";
import AdminBulkUploadLog from "./pages/admin/AdminBulkUploadLog";
import AdminSmartCatalog from "./pages/admin/AdminSmartCatalog";
import AdminTryOnBatchDetail from "./pages/admin/AdminTryOnBatchDetail";
import AdminAttributes from "./pages/admin/AdminAttributes";
import AdminBanners from "./pages/admin/AdminBanners";
import AdminCoupons from "./pages/admin/AdminCoupons";
import AdminReports from "./pages/admin/AdminReports";
import AdminSections from "./pages/admin/AdminSections";
import AdminSizeCharts from "./pages/admin/AdminSizeCharts";
import AdminReturns from "./pages/admin/AdminReturns";
import AdminRefunds from "./pages/admin/AdminRefunds";
import AdminCampaigns from "./pages/admin/AdminCampaigns";
import AdminClothingTypes from "./pages/admin/AdminClothingTypes";
import AdminMeasurementFields from "./pages/admin/AdminMeasurementFields";
import AdminAppointments from "./pages/admin/AdminAppointments";
import AdminAppointmentSlots from "./pages/admin/AdminAppointmentSlots";
import AdminSocial from "./pages/admin/AdminSocial";
import AdminCreators from "./pages/admin/AdminCreators";
import AdminBranding from "./pages/admin/AdminBranding";
import AdminPushBroadcast from "./pages/admin/AdminPushBroadcast";
import AdminBilling from "./pages/admin/AdminBilling";
import AdminWallet from "./pages/admin/AdminWallet";
import AdminUpgrade from "./pages/admin/AdminUpgrade";
import AdminDomain from "./pages/admin/AdminDomain";
import SuperAdminHome from "./pages/superadmin/SuperAdminHome";
import SuperAdminFeatures from "./pages/superadmin/SuperAdminFeatures";
import SuperAdminPlans from "./pages/superadmin/SuperAdminPlans";
import SuperAdminTenants from "./pages/superadmin/SuperAdminTenants";
import SuperAdminAnalytics from "./pages/superadmin/SuperAdminAnalytics";
import SuperAdminSettings from "./pages/superadmin/SuperAdminSettings";
import SuperAdminWallets from "./pages/superadmin/SuperAdminWallets";
import SuperAdminWalletDetail from "./pages/superadmin/SuperAdminWalletDetail";
import SuperAdminPricing from "./pages/superadmin/SuperAdminPricing";
import SuperAdminSubscriptions from "./pages/superadmin/SuperAdminSubscriptions";
import SuperAdminPwaBundles from "./pages/superadmin/SuperAdminPwaBundles";
import SuperAdminFirebase from "./pages/superadmin/SuperAdminFirebase";
import SuperAdminPayments from "./pages/superadmin/SuperAdminPayments";
import SuperAdminMigrateTenant from "./pages/superadmin/SuperAdminMigrateTenant";
import FeatureGate from "./components/common/FeatureGate";
import ScrollToTop from "./components/common/ScrollToTop";
import FloatingCartBar from "./components/store/FloatingCartBar";
import AddToCartDrawer from "./components/store/AddToCartDrawer";
import SellerAppointments from "./pages/seller/SellerAppointments";
import StartStore from "./pages/StartStore";

function StoreLayout({ children }) {
  return (
    <>
      <StoreHeader />
      <main className="min-h-[70vh]">{children}</main>
      <StoreFooter />
      <FloatingCartBar />
      <AddToCartDrawer />
    </>
  );
}

function RoleRoute({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/" replace />;
  if (!roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<StoreLayout><Home /></StoreLayout>} />
      <Route path="/start-store" element={<StartStore />} />
      <Route path="/shop" element={<StoreLayout><Listing /></StoreLayout>} />
      <Route path="/shop/:category" element={<StoreLayout><Listing /></StoreLayout>} />
      <Route path="/product/:slug" element={<StoreLayout><ProductDetail /></StoreLayout>} />
      <Route path="/cart" element={<StoreLayout><Cart /></StoreLayout>} />
      <Route path="/cart/shared/:token" element={<StoreLayout><SharedCartView /></StoreLayout>} />
      <Route path="/t/:tenantSlug/cart/shared/:token" element={<StoreLayout><SharedCartView /></StoreLayout>} />
      <Route path="/wishlist" element={<StoreLayout><Wishlist /></StoreLayout>} />
      <Route path="/checkout" element={<StoreLayout><Checkout /></StoreLayout>} />
      <Route path="/order/:id/success" element={<StoreLayout><OrderSuccess /></StoreLayout>} />
      <Route path="/order/:id" element={<StoreLayout><OrderDetail /></StoreLayout>} />
      <Route path="/account" element={<StoreLayout><Account /></StoreLayout>} />
      <Route path="/campaign/:slug" element={<StoreLayout><CampaignPage /></StoreLayout>} />
      <Route path="/reels" element={<StoreLayout><StyleFeed /></StoreLayout>} />
      <Route path="/creator/:handle" element={<StoreLayout><CreatorPage /></StoreLayout>} />
      <Route path="/legal/privacy" element={<StoreLayout><LegalPage kind="privacy" /></StoreLayout>} />
      <Route path="/legal/terms" element={<StoreLayout><LegalPage kind="terms" /></StoreLayout>} />
      <Route path="/t/:tenantSlug/legal/privacy" element={<StoreLayout><LegalPage kind="privacy" /></StoreLayout>} />
      <Route path="/t/:tenantSlug/legal/terms" element={<StoreLayout><LegalPage kind="terms" /></StoreLayout>} />

      <Route path="/seller/onboard" element={<StoreLayout><SellerOnboard /></StoreLayout>} />
      <Route
        path="/seller/*"
        element={
          <RoleRoute roles={["seller", "admin"]}>
            <DashboardLayout variant="seller">
              <Routes>
                <Route index element={<SellerDashboard />} />
                <Route path="products" element={<SellerProducts />} />
                <Route path="products/new" element={<SellerProductForm />} />
                <Route path="products/:id/edit" element={<SellerProductForm />} />
                <Route path="inventory" element={<SellerInventory />} />
                <Route path="orders" element={<SellerOrders />} />
                <Route path="coupons" element={<SellerCoupons />} />
                <Route path="analytics" element={<SellerAnalytics />} />
                <Route path="appointments" element={<SellerAppointments />} />
                <Route path="settings" element={<SellerSettings />} />
              </Routes>
            </DashboardLayout>
          </RoleRoute>
        }
      />

      <Route
        path="/admin/*"
        element={
          <RoleRoute roles={["admin"]}>
            <DashboardLayout variant="admin">
              <Routes>
                <Route index element={<AdminDashboard />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="customers" element={<AdminCustomers />} />
                <Route path="products/bulk-upload" element={<AdminBulkUpload />} />
                <Route path="products/bulk-upload/:id" element={<AdminBulkUploadLog />} />
                <Route path="products/smart-catalog" element={<AdminSmartCatalog />} />
                <Route path="try-on/batches/:id" element={<AdminTryOnBatchDetail />} />
                <Route path="sellers" element={<AdminSellers />} />
                <Route path="categories" element={<AdminCategories />} />
                <Route path="attributes" element={<AdminAttributes />} />
                <Route path="size-charts" element={<AdminSizeCharts />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="products/new" element={<SellerProductForm />} />
                <Route path="products/:id/edit" element={<SellerProductForm />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="returns" element={<AdminReturns />} />
                <Route path="refunds" element={<AdminRefunds />} />
                <Route path="banners" element={<AdminBanners />} />
                <Route path="sections" element={<AdminSections />} />
                <Route path="campaigns" element={<AdminCampaigns />} />
                <Route path="stitching" element={<FeatureGate featureKey="measurements" title="Stitching is locked"><AdminClothingTypes /></FeatureGate>} />
                <Route path="stitching/:ctypeId/fields" element={<FeatureGate featureKey="measurements" title="Stitching is locked"><AdminMeasurementFields /></FeatureGate>} />
                <Route path="appointments" element={<FeatureGate featureKey="stitching_appointments" title="Appointments is locked"><AdminAppointments /></FeatureGate>} />
                <Route path="appointment-slots" element={<FeatureGate featureKey="stitching_appointments" title="Appointments is locked"><AdminAppointmentSlots /></FeatureGate>} />
                <Route path="social" element={<FeatureGate featureKey="social_reels" title="Reels is locked"><AdminSocial /></FeatureGate>} />
                <Route path="creators" element={<FeatureGate featureKey="social_reels" title="Reels is locked"><AdminCreators /></FeatureGate>} />
                <Route path="branding" element={<AdminBranding />} />
                <Route path="push" element={<FeatureGate featureKey="push_broadcasts" title="Push Broadcasts is locked"><AdminPushBroadcast /></FeatureGate>} />
                <Route path="coupons" element={<AdminCoupons />} />
                <Route path="reports" element={<FeatureGate featureKey="advanced_analytics" title="Reports is locked"><AdminReports /></FeatureGate>} />
                <Route path="billing" element={<AdminBilling />} />
                <Route path="wallet" element={<AdminWallet />} />
                <Route path="upgrade" element={<AdminUpgrade />} />
                <Route path="domain" element={<AdminDomain />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="pingbix" element={<AdminPingbix />} />
              </Routes>
            </DashboardLayout>
          </RoleRoute>
        }
      />

      <Route
        path="/superadmin/*"
        element={
          <RoleRoute roles={["super_admin"]}>
            <DashboardLayout variant="super">
              <Routes>
                <Route index element={<SuperAdminHome />} />
                <Route path="features" element={<SuperAdminFeatures />} />
                <Route path="plans" element={<SuperAdminPlans />} />
                <Route path="tenants" element={<SuperAdminTenants />} />
                <Route path="analytics" element={<SuperAdminAnalytics />} />
                <Route path="wallets" element={<SuperAdminWallets />} />
                <Route path="wallets/:id" element={<SuperAdminWalletDetail />} />
                <Route path="pricing" element={<SuperAdminPricing />} />
                <Route path="subscriptions" element={<SuperAdminSubscriptions />} />
                <Route path="pwa" element={<SuperAdminPwaBundles />} />
                <Route path="firebase" element={<SuperAdminFirebase />} />
                <Route path="payments" element={<SuperAdminPayments />} />
                <Route path="migrate-tenant" element={<SuperAdminMigrateTenant />} />
                <Route path="settings" element={<SuperAdminSettings />} />
              </Routes>
            </DashboardLayout>
          </RoleRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // When the URL is /t/{slug}/..., use that prefix as the BrowserRouter
  // basename so all existing relative routes work unchanged for the tenant.
  let basename = "";
  if (typeof window !== "undefined") {
    const parts = window.location.pathname.split("/").filter(Boolean);
    if (parts[0] === "t" && parts[1]) basename = `/t/${parts[1]}`;
  }
  return (
    <TenantProvider>
      <AuthProvider>
        <CartProvider>
          <BrandingProvider>
            <PWAProvider>
              <FeaturesProvider>
                <BrowserRouter basename={basename}>
                  <ScrollToTop />
                  <AppRoutes />
                  <OfflineBadge />
                  <InstallBanner />
                  <Toaster position="top-right" richColors />
                </BrowserRouter>
              </FeaturesProvider>
            </PWAProvider>
          </BrandingProvider>
        </CartProvider>
      </AuthProvider>
    </TenantProvider>
  );
}

export default App;
